import { useReducer } from "react";
import { getMissingLogo } from "@/controllers/stock";
import { currentLogosConfig } from "@/lib/logos";

const erroredLogos = new Set<number>();
const placeholderLogos = new Set<number>();
// Sids we've already given a one-shot retry, so a genuinely-missing logo
// latches to the text fallback after a single retry instead of thrashing.
const retriedLogos = new Set<number>();
// Bumped once per sid on retry; TableCell appends it to the logo URL as a
// cache-buster so the retry actually re-fetches (a cold logo origin
// hit returns a cached 1×1 placeholder that a same-URL reload would just
// serve again from the browser cache).
const retryVersions = new Map<number, number>();

// Long enough for the getMissingLogo() backend trigger to materialise the
// real logo on the CDN before we re-request it.
const RETRY_DELAY_MS = 5000;
// On the edge a failed logo is not retried in place; after this long a new mount may ask again.
const EDGE_FORGET_MS = 10 * 60 * 1000;

export const useImageHandler = (source: string) => {
    const [, forceUpdate] = useReducer((n: number) => n + 1, 0);

    // One-shot self-heal. Both failure modes latch a logo to a grey ticker
    // box for the whole session: a transient error (cold/slow CDN on the
    // first-load burst → onError) and a 1×1 "not generated yet" placeholder
    // (onLoadingComplete). Either way, ask the backend to generate the logo,
    // then after a delay clear the "missing" flag and bump the cache-buster
    // so the cell re-requests it. This is what fixes "logos missing on first
    // load, fine after a refresh". Retried at most once per sid per session.
    const scheduleLogoRetry = (security_id: number) => {
        if (retriedLogos.has(security_id)) return;
        // The edge records its own misses and sources them on its refresh sweep, not within
        // seconds: a retry there only repeats the 404 (and its console error). Latch instead, and
        // forget the failure after a while so a later mount asks again (it may have been a blip).
        if (currentLogosConfig().source === "edge") {
            retriedLogos.add(security_id);
            setTimeout(() => {
                erroredLogos.delete(security_id);
                placeholderLogos.delete(security_id);
                retriedLogos.delete(security_id);
            }, EDGE_FORGET_MS);
            return;
        }
        retriedLogos.add(security_id);
        getMissingLogo(security_id);
        setTimeout(() => {
            erroredLogos.delete(security_id);
            placeholderLogos.delete(security_id);
            retryVersions.set(security_id, (retryVersions.get(security_id) ?? 0) + 1);
            forceUpdate();
        }, RETRY_DELAY_MS);
    };

    const handleImgError = (security_id: number) => {
        if (!security_id || erroredLogos.has(security_id)) return;
        erroredLogos.add(security_id);
        scheduleLogoRetry(security_id);
        forceUpdate();
    };

    const hasImgError = (security_id: number) => erroredLogos.has(security_id);

    const handleImgLoad = (img: { naturalWidth: number; naturalHeight: number }, security_id?: number) => {
        if (img?.naturalWidth === 1 && img?.naturalHeight === 1 && security_id && !placeholderLogos.has(security_id)) {
            placeholderLogos.add(security_id);
            scheduleLogoRetry(security_id);
            forceUpdate();
        }
    };

    const isPlaceholderImg = (security_id: number) => !!security_id && placeholderLogos.has(security_id);

    // 0 = never retried (no query param → URL byte-identical to today); >0
    // after a retry. TableCell appends `?v=<n>` only when non-zero.
    const imgRetryVersion = (security_id?: number) => (security_id ? (retryVersions.get(security_id) ?? 0) : 0);

    return { hasImgError, isPlaceholderImg, handleImgError, handleImgLoad, imgRetryVersion };
};
